import React, { useState } from 'react';
import { SITE, WEALTH_SERVICES, FINANCE_SERVICES, RISK_SERVICES } from '../data/siteData';
import './Contact.css';
import Icon from '../components/Icon';
import { useSearchParams } from 'react-router-dom';
import home from '../assets/home.jpeg';
import emailjs from '@emailjs/browser';

const ALL_SERVICES = [
  ...WEALTH_SERVICES.map(s => s.short || s.name),
  ...FINANCE_SERVICES.map(s => s.name),
  ...RISK_SERVICES.map(s => s.name),
  'General Enquiry',
];

// Generate time slots from 9:00 AM to 7:00 PM
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 19; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const displayMinute = minute === 0 ? '00' : minute;
      const timeString = `${displayHour}:${displayMinute} ${ampm}`;
      slots.push(timeString);
    }
  }
  return slots;
};

// Get available dates (next 30 days)
const getAvailableDates = () => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 30; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    // Skip Sundays (optional: you can customize which days are available)
    if (date.getDay() !== 0) {
      dates.push(date);
    }
  }
  return dates;
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const preSelected = searchParams.get('service') || '';
  const [form, setForm] = useState({ 
    name: '', 
    phone: '', 
    email: '', 
    service: preSelected, 
    message: '',
    preferredDate: '',
    preferredTime: '',
    timezone: 'IST'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState('');

  const availableDates = getAvailableDates();
  const timeSlots = generateTimeSlots();

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const serviceId = 'service_uejvbmq';
    const templateId = 'template_1dkeo9n';
    const publicKey = '2f9QV6BD7tlGvl9K_';

    // Prepare email template parameters with fallback values
    const templateParams = {
      from_name: form.name || 'Not provided',
      from_email: form.email || 'Not provided',
      phone: form.phone || 'Not provided',
      service: form.service || 'Not selected',
      preferred_date: form.preferredDate || 'Not specified',
      preferred_time: form.preferredTime || 'Not specified',
      message: form.message || 'No message provided'
    };

    console.log('Sending booking data:', templateParams);

    const response = await emailjs.send(serviceId, templateId, templateParams, publicKey);
    
    console.log('Email sent successfully!', response);
    setSubmitted(true);
    setLoading(false);
  } catch (error) {
    console.error('Failed to send email:', error);
    setError('Failed to send your booking. Please try again or call us directly.');
    setLoading(false);
  }
};

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isDateSelected = (date) => {
    return form.preferredDate === formatDate(date);
  };

  const handleDateSelect = (date) => {
    setForm(f => ({ ...f, preferredDate: formatDate(date) }));
    setShowDatePicker(false);
  };

  const changeMonth = (increment) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    const monthName = currentMonth.toLocaleString('default', { month: 'long' });
    const yearName = currentMonth.getFullYear();

    // Empty cells for days before first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-empty"></div>);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isAvailable = availableDates.some(d => 
        d.getDate() === i && 
        d.getMonth() === month && 
        d.getFullYear() === year
      );
      const isSelected = form.preferredDate === formatDate(date);
      
      days.push(
        <div 
          key={i} 
          className={`calendar-day ${isAvailable ? 'available' : 'unavailable'} ${isSelected ? 'selected' : ''}`}
          onClick={() => isAvailable && handleDateSelect(date)}
        >
          {i}
          {isAvailable && <span className="availability-dot"></span>}
        </div>
      );
    }

    return (
      <div className="calendar-popup">
        <div className="calendar-header">
          <button onClick={() => changeMonth(-1)} className="calendar-nav">‹</button>
          <span className="calendar-month-year">{monthName} {yearName}</span>
          <button onClick={() => changeMonth(1)} className="calendar-nav">›</button>
        </div>
        <div className="calendar-weekdays">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-grid">
          {days}
        </div>
        <div className="calendar-footer">
          <span className="calendar-legend">
            <span className="legend-dot available"></span> Available
            <span className="legend-dot selected"></span> Selected
          </span>
          <button onClick={() => setShowDatePicker(false)} className="calendar-close">
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="contact-page page-wrapper">
      {/* Hero */}
      <div className="ct-hero">
        <div className="ct-hero-bg" />
        <div className="ct-hero-inner">
          <div className="ct-hero-content">
            <div className="ct-hero-left">
              <div className="section-label">Get In Touch</div>
              <h1 className="section-title">
                Let's Build Your<br /><span className="green">Financial Future Together</span>
              </h1>
              <p className="section-sub" style={{ marginTop: 20, maxWidth: 600 }}>
                Book a free 30-minute consultation with G.M. Narendra. No obligations, no pressure — just clarity on how to grow and protect your wealth.
              </p>
            </div>
            <div className="ct-hero-right">
              <img 
                src={home}
                alt="Financial Consultation"
                className="ct-hero-image"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="ct-main">
        <div className="ct-main-inner">
          {/* Contact Info */}
          <div className="ct-info">
            <div className="ct-info-card">
              <div className="cti-label">Your Advisor</div>
              <div className="cti-name">G.M. Narendra</div>
              <div className="cti-title">Management Consultant</div>
              <div className="cti-creds">M.Com · MBA · PGDCS</div>

              <div className="cti-contact-list">
                <a href={`tel:${SITE.contact.phone}`} className="cti-contact-item">
                  <div className="cti-icon">📞</div>
                  <div>
                    <div className="cti-item-label">Phone / WhatsApp</div>
                    <div className="cti-item-val">{SITE.contact.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${SITE.contact.email}`} className="cti-contact-item">
                  <div className="cti-icon">✉</div>
                  <div>
                    <div className="cti-item-label">Email</div>
                    <div className="cti-item-val">{SITE.contact.email}</div>
                  </div>
                </a>
                <div className="cti-contact-item">
                  <div className="cti-icon">📍</div>
                  <div>
                    <div className="cti-item-label">Office</div>
                    <div className="cti-item-val">{SITE.contact.address}</div>
                  </div>
                </div>
                <div className="cti-contact-item">
                  <div className="cti-icon">🕐</div>
                  <div>
                    <div className="cti-item-label">Office Hours</div>
                    <div className="cti-item-val">Mon – Sat: 9:00 AM – 7:00 PM</div>
                  </div>
                </div>
              </div>

              <div className="cti-divider" />

              <div className="cti-quick-label">Quick Contact</div>
              <div className="cti-quick-actions">
                <a href={`tel:${SITE.contact.phone}`} className="btn-primary" style={{ flex: 1, justifyContent: 'center', fontSize: 12, padding: '13px 16px' }}>
                  📞 Call Now
                </a>
                <a
                  href={`https://wa.me/91${SITE.contact.phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                  style={{ flex: 1, justifyContent: 'center', fontSize: 12, padding: '13px 16px' }}
                >
                  💬 WhatsApp
                </a>
              </div>
            </div>

            {/* Why talk to us */}
            <div className="ct-why-list">
              <div className="ct-why-title">What to expect in your consultation</div>
              {[
                '30-minute free session — no cost, no obligation',
                'Complete financial health assessment',
                'Personalized product recommendations',
                'Tax optimization suggestions',
                'Clear action plan to start investing',
              ].map((item, i) => (
                <div key={i} className="ct-why-item">
                  <span className="ct-why-check">✦</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="ct-form-wrap">
            {submitted ? (
              <div className="ct-success">
                <div className="ct-success-icon">✅</div>
                <div className="ct-success-title">Thank You, {form.name}!</div>
                <p className="ct-success-text">
                  Your enquiry has been received. G.M. Narendra will contact you at <strong>{form.phone}</strong> within 24 hours.
                </p>
                <div className="ct-booking-details">
                  <p><strong>📅 Preferred Date:</strong> {form.preferredDate || 'Not specified'}</p>
                  <p><strong>🕐 Preferred Time:</strong> {form.preferredTime || 'Not specified'}</p>
                </div>
                <a href={`tel:${SITE.contact.phone}`} className="btn-primary" style={{ marginTop: 24 }}>
                  📞 Call Now for Faster Response
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="ct-form">
                <div className="ct-form-title">Book Free Consultation</div>
                <div className="ct-form-sub">Fill this form and we'll call you back within 24 hours</div>

                {error && (
                  <div className="form-error" style={{
                    background: 'rgba(255,0,0,0.1)',
                    border: '1px solid rgba(255,0,0,0.2)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    color: '#ff6b6b',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}>
                    {error}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your mobile number"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Date Selection */}
                <div className="form-field">
                  <label className="form-label">Preferred Date *</label>
                  <div className="date-picker-wrapper">
                    <input
                      type="text"
                      name="preferredDate"
                      value={form.preferredDate}
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      readOnly
                      required
                      className="form-input date-input"
                      placeholder="Select a date..."
                    />
                    <span className="date-picker-icon" onClick={() => setShowDatePicker(!showDatePicker)}>
                      📅
                    </span>
                    {showDatePicker && renderCalendar()}
                  </div>
                </div>

                {/* Time Selection */}
                <div className="form-field">
                  <label className="form-label">Preferred Time *</label>
                  <select
                    name="preferredTime"
                    value={form.preferredTime}
                    onChange={handleChange}
                    required
                    className="form-input form-select"
                  >
                    <option value="">Select a time slot...</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-label">Service of Interest *</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                    className="form-input form-select"
                  >
                    <option value="">Select a service...</option>
                    <optgroup label="Wealth Management">
                      {WEALTH_SERVICES.map(s => (
                        <option key={s.id} value={s.short}>{s.short}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Finance Management">
                      {FINANCE_SERVICES.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Risk Management">
                      {RISK_SERVICES.map(s => (
                        <option key={s.id} value={s.short || s.name}>{s.short || s.name}</option>
                      ))}
                    </optgroup>
                    <option value="General Enquiry">General Enquiry</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-label">Message / Questions</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    className="form-input form-textarea"
                    placeholder="Tell us about your financial goals, current situation, or any specific questions..."
                    rows={5}
                  />
                </div>

                <button type="submit" className="btn-primary form-submit" disabled={loading}>
                  {loading ? 'Sending...' : 'Book My Free Consultation →'}
                </button>

                <p className="form-note">
                  🔒 Your information is 100% confidential. We never share your data.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="ct-map">
        <div className="ct-map-inner">
          <div className="section-label" id="findus">Find Us</div>
          <h2 className="section-title" style={{ marginBottom: 8 }}><span className="green" id="findus">Visit Our Office</span></h2>
          <p className="section-sub" id="findus">{SITE.contact.address}</p>
          <div className="ct-map-frame" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              title="LMS Office Location"
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3806.1091986737397!2d78.5043182!3d17.4544868!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9bb771291325%3A0x849c51c635343af5!2sLMS%20MARKETING%20AND%20SERVICES!5e0!3m2!1sen!2sin!4v1781588388703!5m2!1sen!2sin"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}